/**
 * Shared look for the public auth pages (login, registration, forgot
 * password). Colours come from the app theme in src/css/style.css
 * (--primary green and its rgba steps); nothing here may introduce a
 * second accent colour.
 */
const FIELD_BORDER = '#b9c0cc';
const TEXT_MUTED = '#6b7280';

export const authField = {
  input: {
    width: '100%',
    padding: '0.6875rem 0.875rem 0.6875rem 2.75rem',
    border: `1px solid ${FIELD_BORDER}`,
    borderRadius: '6px',
    fontSize: '0.9375rem',
    lineHeight: 1.4,
    color: '#1f2937',
    backgroundColor: '#fff',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  },
  icon: {
    position: 'absolute',
    left: '0.875rem',
    color: TEXT_MUTED,
    zIndex: 2,
    pointerEvents: 'none',
  },
  eyeIcon: {
    position: 'absolute',
    right: '0.75rem',
    color: TEXT_MUTED,
    cursor: 'pointer',
    zIndex: 2,
    background: 'none',
    border: 'none',
    padding: '0.25rem',
    display: 'flex',
  },
  label: {
    display: 'block',
    marginBottom: '0.375rem',
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#374151',
  },
};

export const authButton = {
  primary: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--primary)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.9375rem',
    fontWeight: '600',
    transition: 'background-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
    marginTop: '1rem',
    cursor: 'pointer',
  },
  social: {
    width: '100%',
    padding: '0.6875rem',
    backgroundColor: '#fff',
    color: '#374151',
    border: `1px solid ${FIELD_BORDER}`,
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
    cursor: 'pointer',
  },
};

export const authLink = {
  color: 'var(--primary)',
  fontWeight: '600',
  textDecoration: 'none',
};

// Google's brand red for the "G" glyph on the white social button.
export const GOOGLE_ICON_COLOR = '#db4437';

/**
 * Hover / focus / error states for the class names used by the auth pages.
 * Inline styles cannot express pseudo-classes, so every page renders this
 * in a <style> tag.
 */
export const authCss = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .form-input::placeholder { color: #9ca3af; }

  .form-input:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--rgba-primary-2);
    background-color: #fff;
  }

  .form-input:disabled {
    background-color: #f3f4f6;
    color: ${TEXT_MUTED};
  }

  .form-input.error-input {
    border-color: #dc2626;
    background-color: #fef2f2;
  }

  .form-input.error-input:focus {
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15);
  }

  .submit-button:hover:not(:disabled) {
    background: var(--primary-hover);
    transform: translateY(-1px);
    box-shadow: 0 8px 20px var(--rgba-primary-3);
  }

  .submit-button:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px var(--rgba-primary-3);
  }

  .submit-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .social-button:hover:not(:disabled) {
    border-color: var(--primary);
    background-color: var(--rgba-primary-1);
  }

  .social-button:focus-visible {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--rgba-primary-2);
  }

  .social-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .auth-link:hover { color: var(--primary-hover); text-decoration: underline; }

  .back-button:hover { color: var(--primary); }
`;
